export interface DocumentListFilters {
  /** Any of these kinds. `useDocumentsList` sorts them, so one set has one key. */
  kinds?: readonly string[];
  status?: string;
  /** Only documents this user created. */
  mineOnly?: boolean;
  /** Matched against the title, case-insensitively. `useDocumentsList` trims and lowercases it. */
  search?: string;
}

/**
 * Query keys for everything documents.
 *
 * `orgId` AND `userId`, because visibility here is viewer-scoped, not merely
 * org-scoped: the `documents` SELECT policy admits a row on
 * `NOT is_private OR created_by = auth.uid()`. A key carrying the org alone
 * lets one member's private title and body serve the next member of the same
 * org out of cache — and this cache is persisted to localStorage, so the window
 * is the 24h `maxAge`, not the session. (The persistence denylist in
 * `lib/query-persistence.ts` excludes these roots as the second half of the
 * same fix; both legs are needed, per the `archived-constituents` precedent
 * recorded there.)
 *
 * `userId` sits ahead of the sub-key so `all(orgId)` stays a prefix of every
 * key below it and the broad invalidations keep reaching every viewer's entries.
 */
export const documentKeys = {
  all: (orgId: string | undefined) => ['documents', orgId] as const,
  detail: (orgId: string | undefined, userId: string | undefined, documentId: string | undefined) =>
    ['documents', orgId, userId, 'detail', documentId] as const,
  /**
   * Keyed by viewer as well as org: RLS hides other people's private
   * documents, so the number is what THIS viewer can open, not the office's
   * total.
   *
   * Deliberately OUTSIDE the `all` prefix. The count only moves when a row
   * appears, disappears, or changes visibility, yet `all` is invalidated by
   * every write — each 800ms autosave included — and a HEAD count under RLS is
   * a real round-trip per save on a large office. The create, delete and
   * privacy mutations invalidate this key by name instead.
   */
  count: (orgId: string | undefined, userId: string | undefined) =>
    ['document-count', orgId, userId] as const,
  list: (orgId: string | undefined, userId: string | undefined, filters: DocumentListFilters) =>
    ['documents', orgId, userId, 'list', filters] as const,
  /**
   * Every filter combination's paginated list for one viewer.
   *
   * Exists to be RESET rather than invalidated. The list is a keyset infinite
   * query, and invalidating it refetches every cached page with the cursor it
   * was originally fetched with — but the sort column is `updated_at`, so any
   * write reorders the rows those cursors point between, and a row that crosses
   * a page boundary is then either served twice or not at all. Resetting drops
   * the pages and re-walks from the first, which is the only correct response
   * to a reorder.
   */
  lists: (orgId: string | undefined, userId: string | undefined) =>
    ['documents', orgId, userId, 'list'] as const,
  links: (orgId: string | undefined, userId: string | undefined, documentId: string | undefined) =>
    ['documents', orgId, userId, 'links', documentId] as const,
  /**
   * Summary rows for an explicit id set, to re-check a retained selection's version and status.
   * Inside `all`, so any document write refreshes it. Callers pass the ids sorted, so one set has
   * one key whatever order it was selected in.
   */
  summaries: (orgId: string | undefined, userId: string | undefined, ids: readonly string[]) =>
    ['documents', orgId, userId, 'summaries', ids] as const,
  /**
   * What a Pulse-written document was drawn from. Inside `all`, so a Pulse
   * write, which invalidates the root, refreshes the set it replaced.
   */
  sources: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', orgId, userId, 'sources', documentId] as const,
  /**
   * The Pulse AI thread a document was written or refined in. Still under the
   * `documents` root, which is never persisted, but deliberately OUTSIDE the
   * `all` prefix: `all` is invalidated by every autosave, and inside it the
   * approval lookup would run again on each save. The origin changes only when
   * Pulse AI writes, so those paths invalidate `originAll` by name.
   *
   * Keyed on created_at as well as the id: a document deleted and recreated at
   * the same id is another document, with another origin.
   */
  origin: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
    createdAt: string | undefined,
  ) => ['documents', 'origin', orgId, userId, documentId, createdAt] as const,
  /** Every origin of one organization, for the Pulse AI writes that can move one. */
  originAll: (orgId: string | undefined) => ['documents', 'origin', orgId] as const,
  /**
   * The viewer's sidebar-chat thread for a document. Outside `all` for the same reason as
   * `origin`: autosave must not refetch it; a first chat message invalidates it by name.
   */
  pulseThread: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', 'pulse-thread', orgId, userId, documentId] as const,
  /**
   * The comment threads on a document, as this viewer can read them. Outside `all` like
   * `pulseThread`: autosave must not refetch them. The comment mutations and the document's
   * realtime room invalidate it by name. Under the `documents` root, so never persisted.
   */
  comments: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', 'comments', orgId, userId, documentId] as const,
  /**
   * The text anchors of a document's threads. Beside `comments`, outside `all` for the same
   * reason, and refreshed with it by the comment mutations and the room.
   */
  commentAnchors: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', 'comment-anchors', orgId, userId, documentId] as const,
  commentNodeAnchors: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', 'comment-node-anchors', orgId, userId, documentId] as const,
  /**
   * The mutation key every change to one document's comments runs under. While any is in flight
   * the threads are not refetched: a refetch read before a sent comment commits would drop it from
   * the list. The last one to settle refetches for all of them.
   */
  commentActions: (
    orgId: string | undefined,
    userId: string | undefined,
    documentId: string | undefined,
  ) => ['documents', 'comments', orgId, userId, documentId, 'action'] as const,
  forTask: (orgId: string | undefined, userId: string | undefined, taskId: string | undefined) =>
    ['documents', orgId, userId, 'for-task', taskId] as const,
  forConstituent: (
    orgId: string | undefined,
    userId: string | undefined,
    constituentId: string | undefined,
  ) => ['documents', orgId, userId, 'for-constituent', constituentId] as const,
  forCorrespondence: (
    orgId: string | undefined,
    userId: string | undefined,
    correspondenceId: string | undefined,
  ) => ['documents', orgId, userId, 'for-correspondence', correspondenceId] as const,
} as const;
